/**
 * Utilitário para buscar URLs das medalhas do Selo Sebrae de Referência em Atendimento
 * com fallback automático por ano
 */

const BASE_URL = "https://slide1-images.s3.amazonaws.com/public/selo-medalha";
const CURRENT_YEAR = new Date().getFullYear();

/**
 * Tipos de medalhas disponíveis
 */
const MEDAL_TYPES = {
  BRONZE: "seloMedalhaBronze",
  PRATA: "seloMedalhaPrata",
  OURO: "seloMedalhaOuro",
  DIAMANTE: "seloMedalhaDiamante",
};

/**
 * Verifica se uma URL de imagem existe
 * @param {string} url - URL da imagem
 * @returns {Promise<boolean>} - True se a imagem existe, false caso contrário
 */
const imageExists = async (url) => {
  try {
    const response = await fetch(url, { method: "HEAD" });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Busca a URL da medalha com fallback automático por ano
 * @param {string} medalType - Tipo da medalha (BRONZE, PRATA, OURO, DIAMANTE)
 * @returns {Promise<string>} - URL da medalha encontrada
 */
const getMedalUrl = async (medalType) => {
  if (!MEDAL_TYPES[medalType]) {
    throw new Error(`Tipo de medalha inválido: ${medalType}`);
  }

  const baseFileName = MEDAL_TYPES[medalType];

  // 1. Tentar ano atual
  const currentYearUrl = `${BASE_URL}/${baseFileName}${CURRENT_YEAR}.png`;
  if (await imageExists(currentYearUrl)) {
    return currentYearUrl;
  }

  // 2. Tentar ano passado
  const lastYearUrl = `${BASE_URL}/${baseFileName}${CURRENT_YEAR - 1}.png`;
  if (await imageExists(lastYearUrl)) {
    return lastYearUrl;
  }

  // 3. Usar versão default
  return `${BASE_URL}/${baseFileName}.png`;
};

/**
 * Busca todas as URLs das medalhas de uma só vez
 * @returns {Promise<Object>} - Objeto com todas as URLs das medalhas
 */
const getAllMedalsUrls = async () => {
  const [bronze, prata, ouro, diamante] = await Promise.all([
    getMedalUrl("BRONZE"),
    getMedalUrl("PRATA"),
    getMedalUrl("OURO"),
    getMedalUrl("DIAMANTE"),
  ]);

  return {
    bronze,
    prata,
    ouro,
    diamante,
  };
};

/**
 * Versão síncrona que retorna URLs sem verificação de existência
 * Usa sempre o ano atual como primeira opção
 * @param {string} medalType - Tipo da medalha
 * @returns {string} - URL da medalha para o ano atual
 */
const getMedalUrlSync = (medalType) => {
  if (!MEDAL_TYPES[medalType]) {
    throw new Error(`Tipo de medalha inválido: ${medalType}`);
  }

  const baseFileName = MEDAL_TYPES[medalType];
  return `${BASE_URL}/${baseFileName}${CURRENT_YEAR}.png`;
};

/**
 * Versão síncrona que retorna todas as URLs das medalhas
 * @returns {Object} - Objeto com todas as URLs das medalhas para o ano atual
 */
const getAllMedalsUrlsSync = () => {
  return {
    bronze: getMedalUrlSync("BRONZE"),
    prata: getMedalUrlSync("PRATA"),
    ouro: getMedalUrlSync("OURO"),
    diamante: getMedalUrlSync("DIAMANTE"),
  };
};

export {
  getMedalUrl,
  getAllMedalsUrls,
  getMedalUrlSync,
  getAllMedalsUrlsSync,
  MEDAL_TYPES,
};
